import { ActionFunctionArgs, LoaderFunctionArgs, redirect } from "react-router-dom";
import { saveAs } from "file-saver";

import { myAxios } from "../common/functions";
import { appendField } from "../common/FilterFrame/FilterField";
import { fields } from "./WorkFilterForm";
import { buildDefaultState } from "../common/FilterFrame/FilterModal";
import { extraFields } from "./Works";

export const WORK_EXPORT_COLUMNS = [
    { key: "projectName", label: "Project Name" },
    { key: "category", label: "Category" },
    { key: "subCategory", label: "Sub Category" },
    { key: "location", label: "Location" },
    { key: "startTime", label: "Start Time" },
    { key: "endTime", label: "End Time" },
    { key: "allDay", label: "All Day" },
    { key: "supervisorName", label: "Supervisor" },
    { key: "siteContactName", label: "Site Contact" },
    { key: "cadRequired", label: "CAD Required" },
    { key: "reportRequired", label: "Report Required" },
    { key: "waiveJSA", label: "Waive JSA" },
    { key: "leadName", label: "Lead" },
    { key: "description", label: "Description" },
    { key: "void", label: "Void" },
    { key: "voidReason", label: "Void Reason" },
    { key: "creatorName", label: "Creator" },
    { key: "createdAt", label: "Created At" },
    { key: "updaterName", label: "Updater" },
    { key: "updatedAt", label: "Updated At" },
];

export async function worksLoader({ request }: LoaderFunctionArgs) {
    const url = new URL(request.url);
    let shouldRedirect = false;
    for (const [key] of url.searchParams) {
        if(![...Object.keys(buildDefaultState(fields)), ...extraFields].includes(key)){
            url.searchParams.delete(key);
            shouldRedirect = true;
        }
    }
    if (!url.searchParams.get("page")) {
        url.searchParams.set("page", "1");
        shouldRedirect = true;
    }
    if (!url.searchParams.get("limit")) {
        url.searchParams.set("limit", "10");
        shouldRedirect = true;
    }
    if (shouldRedirect) {
        throw redirect(url.pathname + "?" + url.searchParams.toString());
    }
    const page  = Number(url.searchParams.get("page"));
    const limit = Number(url.searchParams.get("limit"));
    const formData = new FormData();
    formData.append("page", String(page));
    formData.append("limit", String(limit));
    for (const field of fields) {
        appendField(formData, url.searchParams, field);
    }

    const res = await myAxios({ fileName: "works/readWorks.php", formData });
    if (!res) throw redirect("..");
    return res.data;
}

export async function workLoader({ params }: LoaderFunctionArgs) {
    const { workId } = params;
    if (!workId) throw redirect("/Works");
    const formData = new FormData();
    formData.append("id", workId);
    let res = await myAxios({ fileName: "works/readWork.php", formData });
    let data = res?.data;
    if (!res || !data) {
        res = await myAxios({ fileName: "works/readWorks.php", formData });
        data = res?.data;
    }
    if (!res || !data) throw redirect("..");
    return res.data;
}

const urlMap = {
    Add: "works/createWork.php",
    Edit: "works/updateWork.php",
} as const;

export async function workAction({ request, params }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => controller.abort());

    const formData = await request.formData();
    const action = String(formData.get("action") ?? "") as keyof typeof urlMap;
    const fileName = urlMap[action];
    if (!fileName) {
        window.alert("Invalid Keyword");
        return null;
    }

    if (!formData.get("waiveJSA")) {
        formData.set("waiveJSA", "0");
    }
    if (!formData.get("allDay")) {
        formData.set("allDay", "");
    }

    const res = await myAxios({ fileName, formData, signal: controller.signal });
    if (!res) return null;

    const nextId = String(res.data?.id ?? res.data?.workId ?? params.workId ?? "");
    throw redirect(nextId ? `/Works/${nextId}` : "/Works");
}

export async function deleteWorkAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => controller.abort());

    const formData = await request.formData();
    if (!formData.get("id")) {
        throw new Response("Missing id", { status: 400 });
    }

    const res = await myAxios({
        fileName: "works/deleteWork.php",
        formData,
        signal: controller.signal,
    });
    if (!res) throw redirect("..");
    throw redirect("../..");
}

export async function voidWorkAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => controller.abort());

    const formData = await request.formData();
    if (!formData.get("id")) {
        throw new Response("Missing id", { status: 400 });
    }

    await myAxios({
        fileName: "works/voidWork.php",
        formData,
        signal: controller.signal,
    });
    throw redirect("..");
}

export async function restoreWorkAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => controller.abort());

    const formData = await request.formData();
    if (!formData.get("id")) {
        throw new Response("Missing id", { status: 400 });
    }

    await myAxios({
        fileName: "works/restoreWork.php",
        formData,
        signal: controller.signal,
    });
    throw redirect("..");
}

export async function workExportAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => controller.abort());

    const url = new URL(request.url);
    const formData = await request.formData();
    for (const field of fields) {
        appendField(formData, url.searchParams, field);
    }

    const res = await myAxios({
        fileName: "/works/exportWorks.php",
        formData,
        responseType: "blob",
        signal: controller.signal,
    });
    if (!res) throw redirect(`..?${url.searchParams.toString()}`);

    const disposition = res.headers["content-disposition"];
    let filename = "works.xlsx";
    if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) filename = decodeURIComponent(match[1]);
    }

    saveAs(
        new Blob([res.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        filename
    );
    throw redirect(`..?${url.searchParams.toString()}`);
}
